"use client";

import { motion, useScroll, useSpring } from "motion/react";
import React from "react";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import type { ScrollBarProps } from "./types";

function ScrollBar({
  progressProps,
  ref,
  className,
  children,
  ...props
}: ScrollBarProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  const { scrollYProgress } = useScroll(
    children ? { container: containerRef } : undefined,
  );

  const scaleX = useSpring(scrollYProgress, {
    stiffness: 250,
    damping: 40,
    bounce: 0,
  });

  React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

  return (
    <React.Fragment>
      <motion.div
        data-slot="scroll-progress"
        {...progressProps}
        style={{ scaleX }}
        className={cn(
          "fixed z-50 top-0 inset-x-0 h-1 bg-primary origin-left",
          progressProps?.className,
        )}
      />

      {containerRef && (
        <div
          ref={containerRef}
          data-slot="scroll-bar-container"
          className={cn("overflow-y-auto h-full", className, classes)}
          {...native}
        >
          {children}
        </div>
      )}
    </React.Fragment>
  );
}

ScrollBar.displayName = "ScrollBar";

export default ScrollBar;
